<template>
  <div class="published-list-item-actions">
    <template v-if="!item.is_revoked">
      <template v-if="isReleased && userHasAllPermissions([ACCESS.NEWS.REPUBLISH])">
        <div title="Повтор">
          <PsButton
            circle
            :border="false"
            class="action-button"
            @click.stop="emit('republish')"
          >
            <PsIcon
              name="actions-republish"
              :size="24"
              color="#0069e4"
            />
          </PsButton>
        </div>
        <CodesPopover
          :loading="isLoadingRepublishUnedited"
          :open="showCodesPopover"
          @cancel="showCodesPopover = false"
          @submit="onRepublishUnedited"
        >
          <div title="Добавить коды">
            <PsButton
              circle
              :border="false"
              class="action-button"
              @click.stop="showCodesPopover = true"
            >
              <PsIcon
                name="actions-add"
                :size="24"
                color="#0069e4"
              />
            </PsButton>
          </div>
          <template #main>
            <NewsCodes
              :item="props.item"
              @update="onCodesSelectionUpdate"
            />
          </template>
        </CodesPopover>
      </template>
      <div
        v-if="userHasAllPermissions([ACCESS.NEWS.EXTENSION])"
        title="Расширить"
      >
        <PsButton
          circle
          :border="false"
          class="action-button"
          @click.stop="emit('extend')"
        >
          <PsIcon
            name="actions-extend"
            :size="24"
            color="#0069e4"
          />
        </PsButton>
      </div>
    </template>
    <div title="Открыть новость">
      <PsButton
        circle
        :border="false"
        class="action-button"
        @click.stop="emit('open')"
      >
        <PsIcon
          name="actions-show-more"
          :size="24"
          color="#0069e4"
        />
      </PsButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, inject } from 'vue'
import { noop } from 'lodash-es'
import { ElNotification } from 'element-plus'
import PsButton from '@/ContextTab/components/UI/Button/PsButton.vue'
import PsIcon from '@/ContextTab/components/UI/PsIcon.vue'
import CodesPopover from '@/ContextTab/modules/news/codes/components/CodesPopover.vue'
import NewsCodes from '@/ContextTab/modules/news/codes/components/NewsCodes.vue'
import { useAccess } from '@/ContextTab/stores/auth/useAccess'
import { ACCESS } from '@/lib/accessCodes'
import { EQuadboxState } from '@/ContextTab/components/UI/types'
import { useNewsEditorStore } from '@/stores/newsEditor.ts'
import { NotificationHandlerKey } from '@/injectionKeys.ts'
import type { CodeSelectState } from '@/ContextTab/modules/news/codes/types'
import type { NewsItemPublished } from '@/ContextTab/stores/types'


const props = defineProps<{
  item: NewsItemPublished
  isReleased: boolean
}>()

const emit = defineEmits<{
  (e: 'republish'): void
  (e: 'open'): void
  (e: 'extend'): void
  (e: 'onTogglePopover', isOpen: boolean): void
}>()

const { onError } = inject(NotificationHandlerKey, { onError: noop })

const showCodesPopover = ref(false)
const isLoadingRepublishUnedited = ref(false)
const checkedState = ref<CodeSelectState>({})

const { userHasAllPermissions } = useAccess()

const { republishUnedited } = useNewsEditorStore()

watch(showCodesPopover, (next) => {
  emit('onTogglePopover', next)
})

const onCodesSelectionUpdate = (state: CodeSelectState) => {
  checkedState.value = state
}

const onRepublishUnedited = async () => {
  isLoadingRepublishUnedited.value = true

  try {
    const ids = Object.entries(checkedState.value)
      .filter(([_, state]) => state === EQuadboxState.included)
      .map(([id, _]) => Number(id))

    await republishUnedited(props.item.id, ids)

    ElNotification.success('Новость переопубликована без изменений')
  } catch (e) {
    onError(e)
  } finally {
    showCodesPopover.value = false
    isLoadingRepublishUnedited.value = false
  }
}
</script>

<style scoped>
.published-list-item-actions {
  --left-gradient-width: 60px;

  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  padding-left: calc(var(--left-gradient-width) + 20px);

  background: linear-gradient(
    to right,
    transparent,
    rgba(232, 238, 253, 0.75) var(--left-gradient-width)
  );

  .action-button {
    --button--hover--background: hsl(223, 84%, 85%);
  }
}
</style>
